import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { navigateThenReloadOnNative } from 'src/app/commons/utils/session-reset';
import { InventoryModuleService } from 'src/app/services/inventory-module/inventory-module.service';
import { PermissionService } from 'src/app/services/permission.service';
import { ProcessModuleService } from 'src/app/services/process-module/process-module.service';
import { SideBarMenuService } from 'src/app/services/side-bar-menu.service';

/**
 * "The plan became active" (2026-10-04): ONE routine for what an already-open session must do after the server activated a
 * subscription, so the card-success page and a bank-transfer / cheque approval behave the same.
 *
 * It refreshes, without a reload: the sidebar menu, the permission set, and the Inventory / Process module visibility
 * (loaded once at app start, so a plan upgrade left those areas hidden before). On the phone app, continueToDashboard() then
 * reloads once at the target screen so the header, sidebar, profile picture and company details start fresh (see
 * navigateThenReloadOnNative). The website only navigates.
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionActivationService {
  private readonly sideBarMenuService = inject(SideBarMenuService);
  private readonly permissionService = inject(PermissionService);
  private readonly inventoryModuleService = inject(InventoryModuleService);
  private readonly processModuleService = inject(ProcessModuleService);

  /** Refresh menu, rights and module visibility. Completes after the fresh permission set is loaded. */
  refreshAccess(): Observable<unknown> {
    this.sideBarMenuService.requestMenuRefresh();
    this.inventoryModuleService.refresh();
    this.processModuleService.refresh();
    return this.permissionService.refreshPermissions();
  }

  /** Refresh, then go to the dashboard (navigate only after the new rights are loaded, so the guards see the new plan). */
  continueToDashboard(router: Router): void {
    this.refreshAccess().subscribe({
      complete: () => navigateThenReloadOnNative(router, '/dashboard-v5'),
    });
  }
}
